/**
 * Teacher Dashboard
 *
 * Lab-grade, professional interface for monitoring student progress,
 * managing assignments, and tracking mastery across concepts.
 */

import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import styles from './TeacherDashboard.module.css';
import { MasteryHeatmap, type Student, type Concept, type MasteryData } from '../components/teacher/MasteryHeatmap';

// Domain types
export type ExperimentDomain = 'physics' | 'chemistry' | 'biology';

// Class data structure
export interface ClassData {
  id: string;
  name: string;
  subject: ExperimentDomain;
  studentCount: number;
}

// Recent activity entry
export interface ActivityEntry {
  id: string;
  type: 'submission' | 'assignment' | 'alert';
  title: string;
  timestamp: Date;
}

// Quick stats
export interface TeacherStats {
  totalStudents: number;
  activeAssignments: number;
  avgMastery: number;
  pendingSubmissions: number;
}

export interface TeacherDashboardProps {
  // All data props are optional - component shows empty states when not provided
  classes?: ClassData[];
  students?: Student[];
  concepts?: Concept[];
  masteryData?: MasteryData;
  stats?: TeacherStats;
  recentActivity?: ActivityEntry[];
}

export function TeacherDashboard({
  classes = [],
  students = [],
  concepts = [],
  masteryData,
  stats,
  recentActivity = [],
}: TeacherDashboardProps) {
  const [selectedClass, setSelectedClass] = useState(classes[0]?.id ?? '');

  // Filter students and concepts by selected class
  const filteredStudents = useMemo(() => {
    if (!selectedClass) return students;
    return students; // TODO: Filter by class when class association is implemented
  }, [selectedClass, students]);

  // Compute display stats from props or derive from data
  const displayStats = useMemo(() => {
    if (stats) return stats;

    // Derive stats from available data
    const totalStudents = filteredStudents.length;
    const avgMastery = masteryData
      ? Math.round(
          Object.values(masteryData).flatMap((studentScores: Record<string, number>) =>
            Object.values(studentScores).reduce((sum: number, score: number) => sum + score, 0) /
            Object.values(studentScores).length
          ).reduce((sum: number, score: number) => sum + score, 0) / Object.keys(masteryData).length
        )
      : 0;

    return {
      totalStudents,
      activeAssignments: 0,
      avgMastery,
      pendingSubmissions: 0,
    };
  }, [stats, filteredStudents, masteryData]);

  // Check if dashboard has any data
  const hasClasses = classes.length > 0;
  const hasHeatmapData = filteredStudents.length > 0 && concepts.length > 0 && masteryData;
  const hasActivity = recentActivity.length > 0;

  // Navigation cards for teacher actions
  const navigationCards = [
    {
      title: 'Class Overview',
      description: 'View detailed class information and student roster',
      icon: (
        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
        </svg>
      ),
      to: '/teacher/class',
    },
    {
      title: 'Assignments',
      description: 'Create and manage assignments, track submissions',
      icon: (
        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01" />
        </svg>
      ),
      to: '/teacher/assignments',
    },
    {
      title: 'Create Assignment',
      description: 'Build a new experiment assignment for your students',
      icon: (
        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M12 4v16m8-8H4" />
        </svg>
      ),
      to: '/teacher/assignments/new',
    },
    {
      title: 'Curriculum Map',
      description: 'View and organize the learning progression',
      icon: (
        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M9 20l-5.447-2.724A1 1 0 013 16.382V5.618a1 1 0 011.447-.894L9 7m0 13l6-3m-6 3V7m6 10l4.553 2.276A1 1 0 0021 18.382V7.618a1 1 0 00-.553-.894L15 4m0 13V4m0 0L9 7" />
        </svg>
      ),
      to: '/teacher/curriculum',
    },
  ];

  return (
    <div className={styles.container}>
      <main className={styles.main}>
        {/* Header */}
        <header className={styles.header}>
          <h1 className={styles.headerTitle}>Teacher Dashboard</h1>
          <p className={styles.headerSubtitle}>
            Monitor student progress and track mastery across concepts
          </p>
        </header>

        {/* Empty State - No Classes */}
        {!hasClasses && (
          <div className={styles.emptyState}>
            <svg className={styles.emptyStateIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
              <path strokeLinecap="round" strokeLinejoin="round" d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
            </svg>
            <h2 className={styles.emptyStateTitle}>No Classes Yet</h2>
            <p className={styles.emptyStateText}>
              Get started by creating your first class to track student progress.
            </p>
            <button className={styles.emptyStateButton}>
              Create Your First Class
            </button>
          </div>
        )}

        {hasClasses && (
          <div className={styles.dashboardGrid}>
            {/* Class Selector */}
            <div className={styles.classSelector}>
              <label htmlFor="classSelect" className={styles.classLabel}>
                Selected Class:
              </label>
              <select
                id="classSelect"
                value={selectedClass}
                onChange={(e) => setSelectedClass(e.target.value)}
                className={styles.classSelect}
              >
                {classes.map(cls => (
                  <option key={cls.id} value={cls.id}>
                    {cls.name} ({cls.studentCount} students)
                  </option>
                ))}
              </select>
            </div>

            {/* Quick Stats */}
            <section className={styles.stats}>
              <div className={styles.statCard}>
                <span className={styles.statNumber}>{displayStats.totalStudents}</span>
                <span className={styles.statLabel}>Total Students</span>
              </div>
              <div className={styles.statCard}>
                <span className={styles.statNumber}>{displayStats.activeAssignments}</span>
                <span className={styles.statLabel}>Active Assignments</span>
              </div>
              <div className={styles.statCard}>
                <span className={styles.statNumber}>{displayStats.avgMastery}%</span>
                <span className={styles.statLabel}>Avg Mastery</span>
              </div>
              <div className={styles.statCard}>
                <span className={styles.statNumber}>{displayStats.pendingSubmissions}</span>
                <span className={styles.statLabel}>Pending Submissions</span>
              </div>
            </section>

            {/* Navigation Cards */}
            <section className={styles.navigation}>
              {navigationCards.map((card) => (
                <Link key={card.to} to={card.to} className={styles.navCard}>
                  <div className={styles.navCardHeader}>
                    <div className={styles.navCardIcon}>{card.icon}</div>
                    <h3 className={styles.navCardTitle}>{card.title}</h3>
                  </div>
                  <p className={styles.navCardDescription}>{card.description}</p>
                </Link>
              ))}
            </section>

            {/* Mastery Heatmap */}
            <section className={`${styles.card} ${styles.heatmapSection}`}>
              <div className={styles.heatmapHeader}>
                <h2 className={styles.heatmapTitle}>Mastery Overview</h2>
                <p className={styles.heatmapSubtitle}>
                  Color-coded visualization of student mastery by concept
                </p>
              </div>
              {hasHeatmapData ? (
                <>
                  <MasteryHeatmap
                    students={filteredStudents}
                    concepts={concepts}
                    masteryData={masteryData}
                  />
                  <div className={styles.legend}>
                    <span className={styles.legendLabel}>Mastery Level:</span>
                    <div className={styles.legendItem}>
                      <span className={`${styles.legendBox} ${styles.legendLow}`}></span>
                      <span className={styles.legendText}>Low (0-40%)</span>
                    </div>
                    <div className={styles.legendItem}>
                      <span className={`${styles.legendBox} ${styles.legendMedium}`}></span>
                      <span className={styles.legendText}>Medium (40-70%)</span>
                    </div>
                    <div className={styles.legendItem}>
                      <span className={`${styles.legendBox} ${styles.legendHigh}`}></span>
                      <span className={styles.legendText}>High (70-100%)</span>
                    </div>
                  </div>
                </>
              ) : (
                <div className={styles.cardEmptyState}>
                  <svg className={styles.cardEmptyStateIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                  </svg>
                  <p className={styles.cardEmptyStateText}>
                    Student mastery data will appear here once students start completing experiments.
                  </p>
                </div>
              )}
            </section>

            {/* Recent Activity */}
            <section className={`${styles.card} ${styles.recentActivity}`}>
              <div className={styles.cardHeader}>
                <h2 className={styles.cardTitle}>Recent Activity</h2>
                {hasActivity && <Link to="/teacher/activity" className={styles.cardAction}>View All</Link>}
              </div>
              {hasActivity ? (
                <div className={styles.activityList}>
                  {recentActivity.map((activity) => (
                    <div key={activity.id} className={styles.activityItem}>
                      <span className={`${styles.activityDot} ${styles[activity.type]}`}></span>
                      <div className={styles.activityInfo}>
                        <h4 className={styles.activityTitle}>{activity.title}</h4>
                        <p className={styles.activityTime}>
                          {activity.timestamp.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className={styles.cardEmptyState}>
                  <svg className={styles.cardEmptyStateIcon} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <p className={styles.cardEmptyStateText}>No recent activity to display</p>
                </div>
              )}
            </section>
          </div>
        )}
      </main>
    </div>
  );
}
